import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function SelectRole() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [role, setRole] = useState("buyer");
  const [interests, setInterests] = useState("");
  const [company, setCompany] = useState("");
  const [busy, setBusy] = useState(false);

  // If the user somehow lands here without needing selection, redirect.
  if (user && user !== false && !user.needs_role_selection) {
    const dest = user.role === "seller" ? "/seller" : user.role === "admin" ? "/admin" : "/discover";
    nav(dest, { replace: true });
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { role };
      if (role === "buyer" && interests) {
        payload.interests = interests.split(",").map((s) => s.trim()).filter(Boolean);
      }
      if (role === "seller" && company) payload.company = company;
      const { data } = await api.post("/auth/select-role", payload);
      await refresh();
      toast.success("Welcome to Vismarks!");
      const stored = localStorage.getItem("vsk_next");
      if (role === "buyer" && stored && stored.startsWith("/")) {
        localStorage.removeItem("vsk_next");
        nav(stored, { replace: true });
        return;
      }
      nav(role === "seller" ? (data?.founders ? "/founders-welcome" : "/seller") : "/discover", { replace: true });
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-6 lg:p-12">
      <form onSubmit={submit} className="w-full max-w-md">
        <div className="text-xs uppercase tracking-widest text-[#007AFF]">One last step</div>
        <h1 className="mt-2 font-black tracking-tight text-3xl">Pick your role</h1>
        <p className="text-neutral-400 mt-2 text-sm">
          Buyers get an AI-curated feed. Sellers get always-on visibility on every SKU.
        </p>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <RoleTile
            active={role === "buyer"}
            onClick={() => setRole("buyer")}
            title="Buyer"
            body="AI-matched discovery"
            testId="select-role-buyer"
          />
          <RoleTile
            active={role === "seller"}
            onClick={() => setRole("seller")}
            title="Seller"
            body="Publish + analytics"
            testId="select-role-seller"
          />
        </div>

        {role === "buyer" ? (
          <div className="mt-6">
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Interests (comma-separated)</Label>
            <Input
              data-testid="select-role-interests"
              value={interests}
              onChange={(e) => setInterests(e.target.value)}
              className="mt-2 bg-[#121212] border-[#27272A]"
              placeholder="electronics, home decor, watches"
            />
          </div>
        ) : (
          <div className="mt-6">
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Company (optional)</Label>
            <Input
              data-testid="select-role-company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className="mt-2 bg-[#121212] border-[#27272A]"
              placeholder="Acme Trading Co."
            />
          </div>
        )}

        <Button
          type="submit"
          disabled={busy}
          data-testid="select-role-submit"
          className="mt-8 w-full bg-white text-black hover:bg-neutral-200 rounded-full h-11 font-semibold"
        >
          {busy ? "Saving…" : "Continue"}
        </Button>
      </form>
    </div>
  );
}

function RoleTile({ active, onClick, title, body, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`text-left rounded-md p-4 border ${active ? "border-[#007AFF] bg-[#007AFF]/10" : "border-[#27272A] bg-[#121212] hover:border-[#3f3f46]"}`}
      style={{ transitionProperty: "background-color, border-color", transitionDuration: "200ms" }}
    >
      <div className="font-bold text-white">{title}</div>
      <div className="text-xs text-neutral-400 mt-1">{body}</div>
    </button>
  );
}
